import React, { useState, useEffect } from 'react';
import DayView from './components/DayView';
import ReservationForm from './components/ReservationForm';
import Settings from './components/Settings';
import Search from './components/Search';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineStatus from './components/OfflineStatus';
import { loadReservations, saveReservations, loadSettings, saveSettings } from './utils/storage';

function App() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reservations, setReservations] = useState({});
  const [settings, setSettings] = useState({ startHour: 9, endHour: 18 });
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const loadedReservations = loadReservations();
      const loadedSettings = loadSettings();
      setReservations(loadedReservations);
      setSettings(loadedSettings);
    } catch (error) {
      setError('Dështoi të ngarkojë të dhënat. Ju lutem rifreskoni faqen.');
      console.error('Error loading data:', error);
    }
  }, []);

  const handleAddReservation = (timeSlot, reservation) => {
    try {
      const dateKey = selectedDate;
      const newReservations = {
        ...reservations,
        [dateKey]: {
          ...reservations[dateKey],
          [timeSlot]: reservation
        }
      };
      setReservations(newReservations);
      saveReservations(newReservations);
      setShowForm(false);
      setSelectedTimeSlot(null);
      setError('');
    } catch (error) {
      setError('Dështoi të ruajë rezervimin. Ju lutem provoni përsëri.');
      console.error('Error saving reservation:', error);
    }
  };

  const handleDeleteReservation = (timeSlot) => {
    try {
      const dateKey = selectedDate;
      const newReservations = { ...reservations };
      if (newReservations[dateKey]) {
        delete newReservations[dateKey][timeSlot];
        if (Object.keys(newReservations[dateKey]).length === 0) {
          delete newReservations[dateKey];
        }
      }
      setReservations(newReservations);
      saveReservations(newReservations);
      setError('');
    } catch (error) {
      setError('Dështoi të fshijë rezervimin. Ju lutem provoni përsëri.');
      console.error('Error deleting reservation:', error);
    }
  };

  const handleTimeSlotClick = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setShowForm(true);
  };

  const handleSettingsSave = (newSettings) => {
    try {
      setSettings(newSettings);
      saveSettings(newSettings);
      setError('');
    } catch (error) {
      setError('Dështoi të ruajë cilësimet. Ju lutem provoni përsëri.');
      console.error('Error saving settings:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    // Albanian day and month names
    const days = ['E Diel', 'E Hënë', 'E Martë', 'E Mërkurë', 'E Enjte', 'E Premte', 'E Shtunë'];
    const months = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName}, ${day} ${month} ${year}`;
  };

  const dismissError = () => {
    setError('');
  };

  const formatWorkingHours = () => {
    const start = settings.startHour.toString().padStart(2, '0') + ':00';
    const end = settings.endHour.toString().padStart(2, '0') + ':00';
    return `${start} - ${end}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <OfflineStatus />
      <PWAInstallPrompt />
      
      <header className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white shadow-2xl">
        <div className="container mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src="/barbershop-logo-template_59362-50.avif" 
                alt="Studio e Flokëve Logo" 
                className="w-12 h-12 rounded-xl bg-white/10 p-2"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  Studio e Flokëve
                </h1>
                <p className="text-blue-100 text-sm mt-1 font-medium">
                  Orët e Punës: {formatWorkingHours()}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowSearch(true)}
                className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20"
                title="Kontrollo disponueshmërinë"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20"
                title="Cilësimet"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto p-6 max-w-lg">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl flex justify-between items-center shadow-sm">
            <span className="text-sm font-medium">{error}</span>
            <button 
              onClick={dismissError}
              className="text-red-400 hover:text-red-600 ml-3 transition-colors"
              aria-label="Mbyll gabimin"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-3">
              📅 Zgjidh Datën
            </label>
            <input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-4 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 hover:bg-white transition-all duration-200 font-medium"
            />
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
              🗓️ {formatDate(selectedDate)}
            </h2>
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2 inline-block">
              💡 Kliko në një kohë të lirë për të bërë rezervim
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <DayView
            selectedDate={selectedDate}
            reservations={reservations[selectedDate] || {}}
            onTimeSlotClick={handleTimeSlotClick}
            onDeleteReservation={handleDeleteReservation}
            settings={settings}
          />
        </div>

        {showForm && (
          <ReservationForm
            timeSlot={selectedTimeSlot}
            onSave={handleAddReservation}
            onCancel={() => {
              setShowForm(false);
              setSelectedTimeSlot(null);
            }}
          />
        )}

        {showSettings && (
          <Settings
            settings={settings}
            onSave={handleSettingsSave}
            onClose={() => setShowSettings(false)}
          />
        )}

        {showSearch && (
          <Search
            reservations={reservations}
            selectedDate={selectedDate}
            onClose={() => setShowSearch(false)}
          />
        )}
      </main>

      <footer className="mt-12 p-6 text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <p className="text-gray-600 font-semibold text-sm">Studio e Flokëve - Rezervimet v2.0</p>
          <p className="text-gray-400 text-xs mt-2">✨ Orë fleksibël • 🔍 Kërkim i shpejtë • 📋 Rezervim i thjeshtë</p>
        </div>
      </footer>
    </div>
  );
}

export default App; 